<template>
  <div class="w-full h-full min-h-screen bg-white p-4">
    <!-- Search Form -->
    <b-row v-if="!searchTrackingNumber">
      <b-col cols="12">
        <b-row class="mb-2">
          <b-col cols="12">
            <span class="font-bold text-sm text-blue">Legal Document Advanced Search</span>
            <div class="text-gray text-xs">Quickly check for any information on legal documents here.</div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <b-form-group label="Search by" label-for="searchType" class="ml-2">
              <b-form-select
                id="searchType"
                class="form-select noFocus text-gray text-sm"
                style="padding:6px 5px;"
                v-model="form.type"
                :options="typeList"
                @change="filterByType"
              />
            </b-form-group>
          </b-col>
          <b-col cols="3" v-if="form.type !== 'INSTRUMENT-NUMBER'">
            <b-form-group label="Document type" label-for="docType" class="ml-2">
              <b-form-select
                id="docType"
                class="form-select noFocus text-gray text-sm"
                style="padding:6px 5px;"
                v-model="form.document_type"
                :options="documentTypeList"
                @change="changeUrl(form.document_type)"
              />
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Search" label-for="searchInput" class="ml-2">
              <b-form-input
                id="searchInput"
                class="text-gray text-sm noFocus"
                style="padding:6px 5px;"
                v-model="form.search"
                type="text"
                placeholder="Enter search term..."
                :disabled="loading"
                v-on:keyup.enter="searchDocument"
              />
            </b-form-group>
          </b-col>
          <b-col cols="2" class="d-flex align-items-end">
            <button class="bg-blue text-white px-3 py-1 rounded-sm text-sm" @click="searchDocument" :disabled="loading">
              <b-spinner v-if="loading" small class="mr-1" />
              <i class="fa fa-search mr-1" v-else></i>
              Search
            </button>
          </b-col>
        </b-row>
        <b-row v-if="searchError">
          <b-col cols="12">
            <small class="text-red">{{ searchError }}</small>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Results Section -->
    <div v-if="searchTrackingNumber">
      <b-row class="my-2">
        <b-col cols="4">
          <b-form-group label="Filter Documents" label-for="filterInput" class="ml-2">
            <b-form-input
              id="filterInput"
              class="text-gray text-sm noFocus"
              style="padding:6px 5px;"
              v-model="searchedDocument"
              type="text"
              placeholder="Filter by instrument no, type, subject..."
            />
          </b-form-group>
        </b-col>
        <b-col cols="6"></b-col>
        <b-col cols="2" class="d-flex align-items-end mb-3">
          <button class="bg-white border border-grey text-gray px-3 py-1 rounded-sm text-sm" @click="resetSearch">
            <i class="fa fa-arrow-left mr-1"></i> Back to Search
          </button>
        </b-col>
      </b-row>

      <div class="mx-auto">
        <b-progress value="100" v-if="showResultProgress" variant="#007ac5" height="0.2rem" animated></b-progress>
      </div>

      <b-row>
        <b-col cols="12">
          <table class="min-w-full leading-normal">
            <thead>
            <tr class="border-b border-t border-grey">
              <th class="t-header">#</th>
              <th class="t-header">Instrument Number</th>
              <th class="t-header">Type</th>
              <th class="t-header" width="40%">Subject Matter</th>
              <th class="t-header text-center">Filing Date</th>
              <th class="t-header text-center">Action</th>
            </tr>
            </thead>
            <tbody>
            <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(document, idx) in searchDocuments" :key="idx">
              <td class="t-body">{{ idx + 1 }}</td>
              <td class="t-body text-blue font-bold cursor-pointer hover:underline"
                  @click="showDetails(document)">{{ document.no }}</td>
              <td class="t-body text-uppercase">{{ document.documentType }}</td>
              <td class="t-body text-capitalize">{{ document.subjectMatter }}</td>
              <td class="t-body text-center">{{ dateFormat(document.filingDate) }}</td>
              <td class="t-body text-center">
                <button class="bg-blue text-white px-2 py-1 rounded-sm text-xs" @click="showDetails(document)">
                  <i class="fa fa-eye mr-1"></i>View
                </button>
              </td>
            </tr>
            <tr>
              <td v-if="searchDocuments.length === 0" colspan="6" class="bg-inertgrey text-center py-1">
                <span class="text-sm">No documents found</span>
              </td>
            </tr>
            </tbody>
          </table>
        </b-col>
      </b-row>
    </div>

    <!-- Document Details Modal -->
    <b-modal
      v-model="showDetailsDialog"
      title="Document Details"
      size="xl"
      hide-footer
      centered
    >
      <DocumentInfo :documentDetailsInfo="documentDetailsInfo" />
    </b-modal>
  </div>
</template>

<script>
import { api } from "@/app/services/api.service";
import DocumentInfo from "@/views/home/services/legal-documents/search/DocumentInfo.vue";
import moment from "moment";

export default {
  name: "LegalDocumentSearch",
  components: { DocumentInfo },
  data() {
    return {
      loading: false,
      form: {
        url: null,
        search: null,
        type: "",
        document_type: "",
      },
      typeList: [
        { disabled: true, value: "", text: "--Search by--" },
        { disabled: false, value: "INDIVIDUAL", text: "Individual Party (NIN, Passport Number or Name)" },
        { disabled: false, value: "ENTITY", text: "Corporate Party (BRN, Name)" },
        { disabled: false, value: "INSTRUMENT-NUMBER", text: "Instrument Number / Subject" },
      ],
      documentTypeList: [
        { disabled: true, value: "", text: "--Select Document type--" },
        { disabled: false, value: "agreements", text: "Agreements", url: "search/parties" },
        { disabled: false, value: "guarantee", text: "Guarantee", url: "search/parties" },
        { disabled: false, value: "powers-of-attorney", text: "Powers of Attorney", url: "power-of-attorney/search" },
        { disabled: false, value: "revocation-of-powers-of-attorney", text: "Revocation of Powers of Attorney", url: "search/parties" },
        { disabled: false, value: "affidavit", text: "Affidavit", url: "affidavit/search" },
        { disabled: false, value: "statutory-declaration", text: "Statutory Declaration", url: "affidavit/search" },
        { disabled: false, value: "chattel", text: "Chattel", url: "deed-of-assignment/search" },
        { disabled: false, value: "debenture", text: "Debenture", url: "deed-of-assignment/search" },
        { disabled: false, value: "demand-promissory-note", text: "Demand Promissory Note", url: "demand-promissory-note/search" },
        { disabled: false, value: "report-of-directors", text: "Report of Directors", url: "search/parties" },
        { disabled: false, value: "constitution", text: "Constitution", url: "search/parties" },
        { disabled: false, value: "loan-facility-letter", text: "Loan Facility Letter", url: "search/parties" },
        { disabled: false, value: "deed-of-assignment", text: "Deed of Assignment", url: "deed-of-assignment/search" },
        { disabled: false, value: "deed-of-inter-vivos", text: "Deed of Inter Vivos", url: "deed-of-assignment/search" },
        { disabled: false, value: "deed-of-variation", text: "Deed of Variation", url: "deed-of-assignment/search" },
        { disabled: false, value: "deed-of-gift-inter-vivos", text: "Deed of Gift / Inter Vivos", url: "deed-of-assignment/search" },
        { disabled: false, value: "deed-of-indemnity", text: "Deed of Indemnity", url: "deed-of-assignment/search" },
        { disabled: false, value: "deed-of-trust", text: "Deed of Trust", url: "deed-of-assignment/search" },
        { disabled: false, value: "deed-poll", text: "DeedPoll", url: "deed-poll/search" },
        { disabled: false, value: "mortgage-deed", text: "Mortgage Deed", url: "search/parties" },
        { disabled: false, value: "spousal-consent", text: "Spousal Consent", url: "search/parties" },
        { disabled: false, value: "will", text: "Will", url: "search/parties" },
        { disabled: false, value: "minutes", text: "Minutes", url: "search/parties" },
        { disabled: false, value: "independent-advise-form", text: "Independent Advise Form", url: "search/parties" },
        { disabled: false, value: "resolution-as-document", text: "Resolution as document", url: "search/parties" },
        { disabled: false, value: "others", text: "Others", url: "search/parties" },
      ],
      searchError: null,
      resultData: [],
      searchedDocument: null,
      showResultProgress: false,
      showDetailsDialog: false,
      documentDetailsInfo: {},
      searchTrackingNumber: null,
      rows: {
        odd: "border-t border-grey hover:bg-header_grey",
        even: "border-t border-grey hover:bg-header_grey"
      },
    };
  },
  mounted() {
    this.searchTrackingNumber = this.$route.query.id;
    if (this.$route.query.id) {
      this.fetchLegalDocumentSearchDetails(this.$route.query.id);
    }
  },
  methods: {
    filterByType(data) {
      if (data === "INSTRUMENT-NUMBER") {
        this.form.document_type = "";
      }
    },
    changeUrl(document_type) {
      if (document_type) {
        let r = this.documentTypeList.find(lst => lst.value === document_type);
        this.form.url = r?.url;
      }
    },
    dateFormat(date) {
      return moment(date).format("DD-MM-YYYY");
    },
    resetSearch() {
      this.searchTrackingNumber = null;
      this.resultData = [];
      this.searchedDocument = null;
      this.form.search = null;
    },
    searchDocument() {
      if (this.searchTrackingNumber) {
        this.warningToast("Warning", "You can't search!");
        return;
      }

      if (this.form.type === "INSTRUMENT-NUMBER") {
        this.loading = true;
        this.searchLegalDocument({
          url: "search",
          search: this.form.search?.trim(),
          document_type: "INSTRUMENT-NUMBER",
          type: "",
        });
      } else {
        if (!this.form.type) {
          this.warningToast("Warning", "Select a search category!");
          return;
        }
        if (!this.form.url) {
          this.warningToast("Warning", "Select a document type!");
          return;
        }
        this.loading = true;
        this.searchLegalDocument(this.form);
      }
    },
    async searchLegalDocument(data) {
      try {
        const response = await api().get(
          `legal-documents/${data.url.trim()}?search=${data.search.trim()}&type=${data.type.trim()}&document_type=${data.document_type.trim()}`
        );
        if (response) {
          let searchRslt = response.data.data;
          if (searchRslt) {
            let trackingNo = typeof searchRslt === "string" ? searchRslt : (searchRslt.trackingNumber || searchRslt);
            this.searchTrackingNumber = trackingNo;
            if (trackingNo && typeof trackingNo === "string") {
              await this.fetchLegalDocumentSearchDetails(trackingNo);
            } else {
              this.resultData = searchRslt;
            }
          } else {
            this.warningToast("Warning", "No result found!");
          }
          this.loading = false;
        }
      } catch {
        this.loading = false;
        this.warningToast("Warning", "No result found!");
      }
    },
    async fetchLegalDocumentSearchDetails(id) {
      try {
        this.showResultProgress = true;
        const response = await api().get(`legal-documents/search/results/details?trackingNo=${id}`);
        if (response) {
          let data = response.data.data;
          this.resultData = data;
          this.form.search = data.search;
          this.form.type = data.documentType === "INSTRUMENT-NUMBER" ? data.documentType : data.type;
          this.form.document_type = data.documentType === "INSTRUMENT-NUMBER" ? "" : data.documentType;
          this.showResultProgress = false;
          this.loading = false;
        }
      } catch (error) {
        this.showResultProgress = false;
        this.loading = false;
        this.warningToast("Warning", error.response?.data?.message);
      }
    },
    showDetails(document) {
      if (document) {
        this.showDetailsDialog = true;
        this.documentDetailsInfo = document;
      }
    },
  },
  computed: {
    searchDocuments() {
      if (!this.searchedDocument) return this.resultData?.documents ?? [];
      return (this.resultData?.documents ?? []).filter((entry) => {
        const q = this.searchedDocument.toLowerCase();
        return (
          entry.subjectMatter?.toLowerCase().includes(q) ||
          entry.no?.toLowerCase().includes(q) ||
          entry.documentType?.toLowerCase().includes(q) ||
          entry.filingDate?.toLowerCase().includes(q)
        );
      });
    },
  },
};
</script>

<style scoped>
@import "../../../../../assets/styles/table.css";
</style>
